
import { timeoutManager } from './timeoutManager';

class TimeSyncManager { 
  private serverTimeOffset = 0; 
  private lastSyncTime = 0; 
  private readonly SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes 
  private syncInProgress = false; 

  constructor() { 
    // Don't sync immediately on construction to avoid startup errors 
    // syncServerTime will be called on first getServerTime() call 
  }

  private async syncServerTime() {
    // Prevent multiple concurrent sync attempts 
    if (this.syncInProgress) { 
      return; 
    }

    this.syncInProgress = true;

    try {
      const start = Date.now();
      const response = await fetch('/api/time');
      const end = Date.now();
      
      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.warn('Server time endpoint not available, using local time');
        this.lastSyncTime = end;
        timeoutManager.resetRetries('time-sync');
        return;
      }
      
      if (response.ok) {
        const { serverTime } = await response.json();
        const networkDelay = (end - start) / 2;
        const adjustedServerTime = serverTime + networkDelay;
        this.serverTimeOffset = adjustedServerTime - end;
        this.lastSyncTime = end;
        timeoutManager.resetRetries('time-sync');
      } else {
        console.warn('Server time sync failed with status:', response.status);
        this.handleSyncError();
      }
    } catch (error) {
      console.warn('Failed to sync server time, using local time:', error.message);
      this.handleSyncError();
    } finally {
      this.syncInProgress = false;
    }
  }

  private handleSyncError() {
    // Use local time as fallback
    this.lastSyncTime = Date.now();
    
    // Schedule retry with exponential backoff
    timeoutManager.setRetry('time-sync', () => {
      if (!this.syncInProgress) {
        this.syncServerTime();
      }
    });
  }

  getServerTime(): number { 
    const now = Date.now();

    // Only re-sync if it's been too long and we're not already syncing
    if (now - this.lastSyncTime > this.SYNC_INTERVAL && !this.syncInProgress) {
      this.syncServerTime();
    }

    return now + this.serverTimeOffset;
  }

  getServerISOString(): string { 
    return new Date(this.getServerTime()).toISOString(); 
  }

  isStale(timestamp: string, maxAge: number): boolean { 
    const age = this.getAge(timestamp); 
    return age > maxAge; 
  }

  getAge(timestamp: string): number { 
    const timestampMs = new Date(timestamp).getTime(); 
    return this.getServerTime() - timestampMs; 
  }
}

export const TimeSync = new TimeSyncManager();
